import { useCallback, useContext, useState } from "react";
import styles from "./VolumeBundleTemplate.module.css";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import { VolumePreview } from "../VolumeBundle/VolumeBundle";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { AvailableVolumeTemplates } from "../../constants";
import { Button, ButtonGroup, Checkbox, Popover, TextField } from "@shopify/polaris";
import { useFormContext } from "react-hook-form";
import { ColorPicker } from "@shopify/polaris";
import { hslaToHex, hexToHsla, lightenHexColor, darkenHexColor } from "../../../../utilities/parsers";
import useIsMobile from "../../../../hooks/useIsMobile";

const VolumeBundleTemplate = ({
    setOpenVolumeTemplateModal,
    bundleConfig,
    forAiBundle = false,
}: {
    openVolumeTemplateModal: any;
    setOpenVolumeTemplateModal: any;
    bundleConfig: any;
    standAlone?: any;
    forAiBundle?: boolean;
}) => {
    const { setValue, getValues } = useFormContext();
    const { isMobile } = useIsMobile();

    const appConfigContext = useContext(AppConfigContext);
    const [popoverActive, setPopoverActive] = useState<boolean>(false);
    const togglePopoverActive = useCallback(() => setPopoverActive((active) => !active), []);

    const [pendingTemplate, setPendingTemplate] = useState<string | null>(
        getValues("selected_template_volume") || null,
    );

    const handleChangeBundleDesign = (color: keyof typeof volTempColor) => {
        const newBundleDesign: any = volTempColor[color];
        const currentBundlesDesign = getValues("bundles_design") || {};

        setValue(
            "bundles_design",
            {
                ...currentBundlesDesign,
                ...newBundleDesign,
            },
            { shouldDirty: true },
        );
    };

    const handleChangeSelectedTemplate = (template: any) => {
        setValue("selected_template_volume", template, { shouldDirty: true });

        if (template.startsWith("horizontal")) {
            setValue("show_variant_selector", false, { shouldDirty: true });
        } else {
            setValue("show_variant_selector", true, { shouldDirty: true });
        }
        setOpenVolumeTemplateModal(false);
    };

    const [color, setColor] = useState({
        hue: 120,
        brightness: 1,
        saturation: 1,
    });
    const [hexColor, setHexColor] = useState("");

    const handleColorChange = (newColor: any) => {
        setColor(newColor);
        const { hue, brightness, saturation } = newColor;
        const hslaColor = `hsla(${hue}, ${saturation * 100}%, ${brightness * 100}%, 1)`;
        const hex = hslaToHex(hslaColor);
        setHexColor(hex);

        const newTemp = {
            add_to_cart_bg_color: hex,
            add_to_cart_border_color: darkenHexColor(hex, 20),
            add_to_cart_hover_bg_color: darkenHexColor(hex, 20),
            bundle_title_color: hex,
            // product_name_color: "#050505",
            // product_price_color: "#050505",
            border_color: "#050505",
            // highlighted_offer_tag_color: "#F8F8F8",
            // highlighted_price_tag_color: "#050505",
            // total_footer_text_color: "#000000",
            // total_footer_price_text_color: "#000000",
            // total_footer_background_color: "#FFFFFF",
            product_background_color: lightenHexColor(hex, 70),
            product_border_color: hex,
            special_text_background_color: hex,
            special_text_color: newColor.brightness >= 0.5 ? "#050505" : "#ffffff",
            // bundle_widget_bg: "#F2F4F2",
            discount_label_background_color: hex,
            discount_label_color: newColor.brightness >= 0.5 ? "#050505" : "#ffffff",

            free_gift_header_bg_color: hex,
            free_gift_header_color: brightness >= 0.5 ? "#050505" : "#ffffff",
            // free_gift_header_size: 15,
            // free_gift_header_style: "650 normal",
            free_gift_bg_color: lightenHexColor(hex, 70),
            free_gift_text_color: "#303030",
            // free_gift_text_size: 12,
            // free_gift_text_style: "550 normal",
            // free_gift_border: true,
            // free_gift_border_width: 0,
            free_gift_border_color: "#050505",
            free_gift_compare_price_color: "#050505",
            upsell_bg_color: lightenHexColor(hex, 70),
            upsell_text_color: "#303030",
            upsell_price_color: hex,
            upsell_compare_price_color: "#050505",
            quantity_selector_border_color: hex,
            quantity_selector_text_color: hex,
            swatches_select_color: hex,
            variant_selector_text_color: hex,
            show_line_around_title_color: hex,
            timer_background_color: lightenHexColor(hex, 70),
            hover_color: hex,
            discount_label_primary_color: hex,
            discount_label_secondary_color: darkenHexColor(hex, 20),
            savings_summary_text_color: hex,
            savings_summary_background_color: lightenHexColor(hex, 70),
        };

        const currentBundlesDesign = getValues("bundles_design") || {};

        setValue(
            "bundles_design",
            {
                ...currentBundlesDesign,
                ...newTemp,
            },
            { shouldDirty: true },
        );
    };

    return (
        <CustomModal
            open={true}
            title="Choose a Template"
            key={"template_qb"}
            onClose={() => setOpenVolumeTemplateModal(false)}
            primaryAction={{
                content: "Apply",
                onAction: () => {
                    if (pendingTemplate) {
                        handleChangeSelectedTemplate(pendingTemplate);
                    }
                    setOpenVolumeTemplateModal(false);
                },
            }}
            size={forAiBundle ? undefined : "large"}
        >
            <div className={styles.colorWrapper}>
                <h6 className={styles.colorWrapperTitle}>Color Theme</h6>
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        position: "relative",
                    }}
                >
                    <ButtonGroup variant="segmented" noWrap>
                        {(isMobile ? Object.keys(volTempColor).slice(0, 5) : Object.keys(volTempColor)).map(
                            (t: any) => (
                                <Button
                                    size="micro"
                                    onClick={() => {
                                        handleChangeBundleDesign(t);
                                    }}
                                >
                                    {/* @ts-expect-error no react elements allowed */}
                                    <div
                                        style={{
                                            backgroundColor: t,
                                        }}
                                        className={styles.colorBtn}
                                    ></div>
                                </Button>
                            ),
                        )}

                        <Popover
                            active={popoverActive}
                            preferredAlignment="right"
                            activator={
                                <Button
                                    size="micro"
                                    onClick={() => {
                                        togglePopoverActive();
                                    }}
                                >
                                    {/* @ts-expect-error no react elements allowed */}
                                    <div
                                        style={{
                                            height: "20px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            fontWeight: "500",
                                            position: "relative",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                width: "20px",
                                                height: "20px",
                                                border: "2px dotted #d2d5db",
                                                borderRadius: "50%",
                                                fontSize: "16px",
                                            }}
                                        >
                                            +
                                        </span>
                                        <span className={styles.customText}>Custom</span>
                                    </div>
                                </Button>
                            }
                            onClose={togglePopoverActive}
                        >
                            <div
                                style={{
                                    // position: "absolute",
                                    // top: "30px",
                                    // right: "0",
                                    backgroundColor: "#ffffff",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    zIndex: 10,
                                    border: "1px solid lightgray",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                }}
                            >
                                <ColorPicker onChange={handleColorChange} color={color} />
                                <TextField
                                    label=""
                                    autoComplete="off"
                                    placeholder="#ffffff"
                                    maxLength={7}
                                    value={hexColor}
                                    onChange={(val) => {
                                        console._log(val);
                                        setHexColor(val);
                                        if (val.length == 7 && val.startsWith("#")) {
                                            const hslaColor = hexToHsla(val);
                                            handleColorChange(hslaColor);
                                        }
                                    }}
                                />
                            </div>
                        </Popover>
                    </ButtonGroup>
                </div>
            </div>

            <div
                className={styles.mainContainer}
                style={{
                    // @ts-expect-error required
                    "--columns": forAiBundle ? "1fr" : " 1fr 1fr",
                }}
            >
                <div
                    className={`${styles.container} ${styles.volumeCard} ${
                        pendingTemplate === AvailableVolumeTemplates.VERTICAL_MODERN ? styles.volumeCardActive : ""
                    }
  `}
                >
                    <VolumePreview
                        bundleConfig={{
                            ...bundleConfig,
                            selected_template_volume: AvailableVolumeTemplates.VERTICAL_MODERN,
                            // volume_discounts: offersArrVerticalModern,
                            general_settings: {
                                show_powered_by_kefi: false,
                            },
                        }}
                        standAlone={false}
                        appConfigContext={appConfigContext}
                        pId={0}
                    />
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            label=""
                            checked={pendingTemplate === AvailableVolumeTemplates.VERTICAL_MODERN}
                            onChange={() => {
                                setPendingTemplate(AvailableVolumeTemplates.VERTICAL_MODERN);
                            }}
                        />
                    </div>
                </div>

                <div
                    className={`${styles.container} ${styles.volumeCard} ${
                        pendingTemplate === AvailableVolumeTemplates.HORIZONTAL_MODERN ? styles.volumeCardActive : ""
                    }
  `}
                >
                    <VolumePreview
                        bundleConfig={{
                            ...bundleConfig,
                            selected_template_volume: AvailableVolumeTemplates.HORIZONTAL_MODERN,
                            show_variant_selector: false,
                            // volume_discounts: offersArrHorizontalModern,
                            general_settings: {
                                show_powered_by_kefi: false,
                            },
                        }}
                        standAlone={false}
                        appConfigContext={appConfigContext}
                        pId={0}
                    />
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            label=""
                            checked={pendingTemplate === AvailableVolumeTemplates.HORIZONTAL_MODERN}
                            onChange={() => {
                                setPendingTemplate(AvailableVolumeTemplates.HORIZONTAL_MODERN);
                            }}
                        />
                    </div>
                </div>

                <div
                    className={`${styles.container} ${styles.volumeCard} ${
                        pendingTemplate === AvailableVolumeTemplates.HORIZONTAL_BASIC ? styles.volumeCardActive : ""
                    }
  `}
                >
                    <VolumePreview
                        bundleConfig={{
                            ...bundleConfig,
                            selected_template_volume: AvailableVolumeTemplates.HORIZONTAL_BASIC,
                            show_variant_selector: false,
                            // volume_discounts: offersArrHorizontalBasic,
                            general_settings: {
                                show_powered_by_kefi: false,
                            },
                        }}
                        standAlone={false}
                        appConfigContext={appConfigContext}
                        pId={0}
                    />
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            label=""
                            checked={pendingTemplate === AvailableVolumeTemplates.HORIZONTAL_BASIC}
                            onChange={() => {
                                setPendingTemplate(AvailableVolumeTemplates.HORIZONTAL_BASIC);
                            }}
                        />
                    </div>
                </div>

                <div
                    className={`${styles.container} ${styles.volumeCard} ${
                        pendingTemplate === AvailableVolumeTemplates.VERTICAL_BASIC ? styles.volumeCardActive : ""
                    }
  `}
                >
                    <VolumePreview
                        bundleConfig={{
                            ...bundleConfig,
                            selected_template_volume: AvailableVolumeTemplates.VERTICAL_BASIC,
                            // volume_discounts: offersArrVerticalBasic,
                            general_settings: {
                                show_powered_by_kefi: false,
                            },
                        }}
                        standAlone={false}
                        appConfigContext={appConfigContext}
                        pId={0}
                    />
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            label=""
                            checked={pendingTemplate === AvailableVolumeTemplates.VERTICAL_BASIC}
                            onChange={() => {
                                setPendingTemplate(AvailableVolumeTemplates.VERTICAL_BASIC);
                            }}
                        />
                    </div>
                </div>

                <div
                    className={`${styles.container} ${styles.volumeCard} ${
                        pendingTemplate === AvailableVolumeTemplates.VERTICAL_CLASSIC ? styles.volumeCardActive : ""
                    }
  `}
                >
                    <VolumePreview
                        bundleConfig={{
                            ...bundleConfig,
                            selected_template_volume: AvailableVolumeTemplates.VERTICAL_CLASSIC,
                            show_variant_selector: true,
                            // volume_discounts: offersArrVerticalClassic,
                            general_settings: {
                                show_powered_by_kefi: false,
                            },
                        }}
                        standAlone={false}
                        appConfigContext={appConfigContext}
                        pId={0}
                    />
                    <div className={styles.checkboxWrapper}>
                        <Checkbox
                            label=""
                            checked={pendingTemplate === AvailableVolumeTemplates.VERTICAL_CLASSIC}
                            onChange={() => {
                                setPendingTemplate(AvailableVolumeTemplates.VERTICAL_CLASSIC);
                            }}
                        />
                    </div>
                </div>
            </div>
        </CustomModal>
    );
};

export default VolumeBundleTemplate;

export const volTempColor = {
    BLACK: {
        // bundle_title_size: 22,
        bundle_title_color: "#000000",
        swatches_select_color: "#000000",
        variant_selector_text_color: "#000000",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#f0f0f0",
        product_border_color: "#050505",
        add_to_cart_bg_color: "#303030",
        add_to_cart_hover_bg_color: "#000000",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#000000",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#050505",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#050505",
        discount_label_color: "#FFFFFF",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#e3e3e3",
        free_gift_header_color: "#303030",
        // free_gift_header_size: 13,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#f0f0f0",
        free_gift_text_color: "#303030",
        // free_gift_text_size: 12,
        // free_gift_text_style: "650 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#303030",
        upsell_bg_color: "#f0f0f0",
        upsell_text_color: "#303030",
        upsell_price_color: "#000000",
        upsell_compare_price_color: "#303030",
        quantity_selector_border_color: "#050505",
        quantity_selector_text_color: "#000000",
        show_line_around_title_color: "#000000",
        hover_color: "#000000",
        discount_label_primary_color: "#111111",
        discount_label_secondary_color: "#f36a0a",
        savings_summary_text_color: "#000000",
        savings_summary_background_color: "#f0f0f0",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#f0f0f0",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    RED: {
        // bundle_title_size: 22,
        bundle_title_color: "#ff2d2d",
        swatches_select_color: "#ff2d2d",
        variant_selector_text_color: "#ff2d2d",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#ffe4e4",
        product_border_color: "#ff4040",
        add_to_cart_bg_color: "#ff0000",
        add_to_cart_hover_bg_color: "#cc0000",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#cc0000",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#fc5050",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#fc5050",
        discount_label_color: "#FFFFFF",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#ff4040",
        free_gift_header_color: "#F5F5F5",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#ffe4e4",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#ffe4e4",
        upsell_text_color: "#050505",
        upsell_price_color: "#ff2d2d",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#ff4040",
        quantity_selector_text_color: "#ff2d2d",
        show_line_around_title_color: "#ff4040",
        hover_color: "#ff2d2d",
        discount_label_primary_color: "#fc5050",
        discount_label_secondary_color: "#cc0000",
        savings_summary_text_color: "#ff2d2d",
        savings_summary_background_color: "#ffe4e4",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#ffe4e4",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    ORANGE: {
        // bundle_title_size: 22,
        bundle_title_color: "#ff6813",
        swatches_select_color: "#ff6813",
        variant_selector_text_color: "#ff6813",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#ffe7dd",
        product_border_color: "#ff550a",
        add_to_cart_bg_color: "#ff7a00",
        add_to_cart_hover_bg_color: "#cc7a00",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#cc7a00",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#ff550a",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#ff550a",
        discount_label_color: "#FFFFFF",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#ff550a",
        free_gift_header_color: "#F5F5F5",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#ffe7dd",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#ffe7dd",
        upsell_text_color: "#050505",
        upsell_price_color: "#ff6813",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#ff550a",
        quantity_selector_text_color: "#ff6813",
        show_line_around_title_color: "#ff550a",
        hover_color: "#ff6813",
        discount_label_primary_color: "#ff550a",
        discount_label_secondary_color: "#cc7a00",
        savings_summary_text_color: "#ff6813",
        savings_summary_background_color: "#ffe7dd",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#ffe7dd",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    LIME: {
        // bundle_title_size: 22,
        bundle_title_color: "#68ff1a",
        swatches_select_color: "#68ff1a",
        variant_selector_text_color: "#68ff1a",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#eaffdf",
        product_border_color: "#68ff1a",
        add_to_cart_bg_color: "#73ff00",
        add_to_cart_hover_bg_color: "#73cc00",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#73cc00",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#68ff1a",
        special_text_color: "#050505",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#68ff1a",
        discount_label_color: "#050505",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#68ff1a",
        free_gift_header_color: "#050505",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#eaffdf",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#eaffdf",
        upsell_text_color: "#050505",
        upsell_price_color: "#68ff1a",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#68ff1a",
        quantity_selector_text_color: "#68ff1a",
        show_line_around_title_color: "#68ff1a",
        hover_color: "#68ff1a",
        discount_label_primary_color: "#68ff1a",
        discount_label_secondary_color: "#73cc00",
        savings_summary_text_color: "#68ff1a",
        savings_summary_background_color: "#eaffdf",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#eaffdf",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    GREEN: {
        // bundle_title_size: 22,
        bundle_title_color: "#399907",
        swatches_select_color: "#399907",
        variant_selector_text_color: "#399907",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#dffdd0",
        product_border_color: "#399907",
        add_to_cart_bg_color: "#5cd600",
        add_to_cart_hover_bg_color: "#5ca300",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#5ca300",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#399907",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#399907",
        discount_label_color: "#FFFFFF",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#399907",
        free_gift_header_color: "#F5F5F5",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#dffdd0",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#dffdd0",
        upsell_text_color: "#050505",
        upsell_price_color: "#399907",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#399907",
        quantity_selector_text_color: "#399907",
        show_line_around_title_color: "#399907",
        hover_color: "#399907",
        discount_label_primary_color: "#399907",
        discount_label_secondary_color: "#5ca300",
        savings_summary_text_color: "#399907",
        savings_summary_background_color: "#dffdd0",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#dffdd0",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    BLUE: {
        // bundle_title_size: 22,
        bundle_title_color: "#073099",
        swatches_select_color: "#073099",
        variant_selector_text_color: "#073099",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#0049d2",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#0049d2",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#d1ebff",
        product_border_color: "#0067b4",
        add_to_cart_bg_color: "#0096ff",
        add_to_cart_hover_bg_color: "#0096cc",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#0096cc",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#0067b4",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#0067b4",
        discount_label_color: "#ffffff",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#073099",
        free_gift_header_color: "#F5F5F5",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#d1ebff",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#d1ebff",
        upsell_text_color: "#050505",
        upsell_price_color: "#073099",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#0067b4",
        quantity_selector_text_color: "#073099",
        show_line_around_title_color: "#0067b4",
        hover_color: "#073099",
        discount_label_primary_color: "#0067b4",
        discount_label_secondary_color: "#0096cc",
        savings_summary_text_color: "#073099",
        savings_summary_background_color: "#d1ebff",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#d1ebff",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    PURPLE: {
        // bundle_title_size: 22,
        bundle_title_color: "#990783",
        swatches_select_color: "#990783",
        variant_selector_text_color: "#990783",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#fddaf8",
        product_border_color: "#990783",
        add_to_cart_bg_color: "#990783",
        add_to_cart_hover_bg_color: "#660783",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#660783",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#990783",
        special_text_color: "#ffffff",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#990783",
        discount_label_color: "#FFFFFF",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#990783",
        free_gift_header_color: "#F5F5F5",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#fddaf8",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#fddaf8",
        upsell_text_color: "#050505",
        upsell_price_color: "#990783",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#990783",
        quantity_selector_text_color: "#990783",
        show_line_around_title_color: "#990783",
        hover_color: "#990783",
        discount_label_primary_color: "#990783",
        discount_label_secondary_color: "#660783",
        savings_summary_text_color: "#990783",
        savings_summary_background_color: "#fddaf8",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#fddaf8",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
    PINK: {
        // bundle_title_size: 22,
        bundle_title_color: "#ffc0cb",
        swatches_select_color: "#ffc0cb",
        variant_selector_text_color: "#ffc0cb",
        // bundle_title_style: "650 normal",
        // product_name_size: 14,
        // product_name_color: "#050505",
        // product_name_style: "450 normal",
        // product_price_size: 10,
        // product_price_color: "#050505",
        // product_price_style: "350 normal",
        border_color: "#050505",
        // border_width: 2,
        // border_radius: 8,
        // background_color: "#F2F4F2",
        // highlighted_offer_tag_size: 12,
        // highlighted_offer_tag_color: "#F8F8F8",
        // highlighted_offer_tag_style: "550 italic",
        // highlighted_price_tag_size: 14,
        // highlighted_price_tag_color: "#050505",
        // highlighted_price_tag_style: "650 normal",
        // total_footer_text_size: 12,
        // total_footer_text_color: "#000000",
        // total_footer_text_style: "550 normal",
        // total_footer_price_text_size: 14,
        // total_footer_price_text_color: "#000000",
        // total_footer_price_text_style: "750 normal",
        // total_footer_background_color: "#FFFFFF",
        // total_footer_background_style: "450 normal",
        product_background_color: "#fff2f5",
        product_border_color: "#ff37fe",
        add_to_cart_bg_color: "#ff7f9d",
        add_to_cart_hover_bg_color: "#cc7f9d",
        // add_to_cart_border_radius: 8,
        add_to_cart_border_color: "#cc7f9d",
        // add_to_cart_text_color: "#F5F5F5",
        // add_to_cart_text_size: 14,
        // add_to_cart_text_style: "750 normal",
        special_text_background_color: "#ffc0cb",
        special_text_color: "#050505",
        // special_text_font_size: 10,
        // special_text_style: "550 normal",
        // highlighted_offer_tag_background_color: "#053989",
        // bundle_widget_bg: "#F2F4F200",
        // bundle_widget_font_family: "Custom Store Font",
        // product_img_size: "medium",
        // product_img_show: "all",
        // product_img_border_radius: 10,
        discount_label_background_color: "#ffc0cb",
        discount_label_color: "#050505",
        // discount_label_font_size: 10,
        // discount_label_style: "550 normal",
        free_gift_header_bg_color: "#ffc0cb",
        free_gift_header_color: "#050505",
        // free_gift_header_size: 15,
        // free_gift_header_style: "650 normal",
        free_gift_bg_color: "#fff2f5",
        free_gift_text_color: "#050505",
        // free_gift_text_size: 12,
        // free_gift_text_style: "550 normal",
        // free_gift_border: true,
        // free_gift_border_width: 0,
        free_gift_border_color: "#050505",
        free_gift_compare_price_color: "#050505",
        upsell_bg_color: "#fff2f5",
        upsell_text_color: "#050505",
        upsell_price_color: "#ffc0cb",
        upsell_compare_price_color: "#050505",
        quantity_selector_border_color: "#ff37fe",
        quantity_selector_text_color: "#ffc0cb",
        show_line_around_title_color: "#ffc0cb",
        hover_color: "#ffc0cb",
        discount_label_primary_color: "#ffc0cb",
        discount_label_secondary_color: "#cc7f9d",
        savings_summary_text_color: "#ffc0cb",
        savings_summary_background_color: "#fff2f5",
        // timer_color: "#000000",
        // timer_text_color: "#000000",
        timer_background_color: "#fff2f5",
        // timer_font_style: "450 normal",
        // timer_font_size: 14,
    },
};
